/**
 * usePresence — Firebase Realtime Database presence tracking for workspaces.
 *
 * Tracks which users are currently active in a workspace and provides
 * automatic online/offline detection via onDisconnect handlers.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { getDatabase, ref, onValue, set, onDisconnect } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { UserPresence } from '../types';
import { keepList } from '../utils/unchanged';

const HEARTBEAT_INTERVAL = 30_000; // 30 seconds
const STALE_THRESHOLD = 90_000; // 90 seconds — consider user offline if no heartbeat

/**
 * The colours a collaborator can have: the tones of the design system (the `[data-tone]` rules
 * in index.css), without the ink of what has no colour. Each with its colour as a value, for
 * what stores a colour and not a tone (the presence and cursor records); the values are the
 * tones' `--tone`, change both together.
 */
const COLLABORATOR_COLORS = {
  sky: '#8EC9F5',
  coral: '#FF8F85',
  mint: '#7FD9B5',
  lemon: '#F2D23C',
  lavender: '#AB9FF2',
  blush: '#F58BA6',
  peach: '#FFB37A',
  flame: '#FF8A4C',
  orchid: '#D69AEB',
  sage: '#B9D97A',
} as const;
export type CollaboratorTone = keyof typeof COLLABORATOR_COLORS;
const COLLABORATOR_TONES = Object.keys(COLLABORATOR_COLORS) as CollaboratorTone[];

/**
 * A collaborator's tone, for `data-tone`: always the same for the same user, so their avatar
 * in the top bar and their cursor on the canvas have one colour.
 */
export const collaboratorTone = (userId: string): CollaboratorTone => {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash + userId.charCodeAt(i)) | 0;
  }
  return COLLABORATOR_TONES[Math.abs(hash) % COLLABORATOR_TONES.length];
};

/** The same tone as a colour: what is written with a user's presence and cursor. */
export const collaboratorColor = (userId: string): string => COLLABORATOR_COLORS[collaboratorTone(userId)];

export interface UsePresenceResult {
  /**
   * The other users active in the workspace. This user is not listed: that they are here
   * themselves is no news to them, and would render the app once more after every open.
   */
  otherUsers: UserPresence[];
}

/** The users in a workspace whose heartbeat is recent, from what the database holds for it. */
export function presentUsers(data: unknown, now: number): UserPresence[] {
  if (!data || typeof data !== 'object') return [];
  return Object.values(data as Record<string, any>)
    .filter((u: any) => u && u.userId)
    .filter((u: any) => now - (u.lastSeen || 0) < STALE_THRESHOLD)
    .map((u: any) => ({
      userId: u.userId,
      displayName: u.displayName || 'Anonymous',
      photoURL: u.photoURL,
      color: u.color || collaboratorColor(u.userId),
      lastSeen: u.lastSeen || 0,
      isActive: u.isActive !== false,
    }));
}

/** Whether a user would be shown the same (a heartbeat only moves lastSeen). */
const samePresence = (a: UserPresence, b: UserPresence): boolean =>
  a.userId === b.userId && a.displayName === b.displayName && a.photoURL === b.photoURL && a.color === b.color && a.isActive === b.isActive;

/**
 * The list to keep in state after an update: the previous one while the same people are
 * there. Every heartbeat (this user's own, every 30 s, and everyone else's) comes through
 * the listener; none of them is a reason to render.
 */
export const keepPresence = (previous: UserPresence[], next: UserPresence[]): UserPresence[] => keepList(previous, next, samePresence);

export function usePresence(
  projectId: string | null,
  enabled: boolean = true,
): UsePresenceResult {
  const [otherUsers, setOtherUsers] = useState<UserPresence[]>([]);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const myPresenceRef = useRef<ReturnType<typeof ref> | null>(null);

  const cleanup = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    if (myPresenceRef.current) {
      set(myPresenceRef.current, null).catch(() => {});
      myPresenceRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!projectId || !enabled) {
      cleanup();
      setOtherUsers(previous => keepPresence(previous, []));
      return;
    }

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      setOtherUsers(previous => keepPresence(previous, []));
      return;
    }

    let db: ReturnType<typeof getDatabase>;
    try {
      db = getDatabase();
    } catch {
      return;
    }

    // The people of the workspace opened before are not in this one
    setOtherUsers(previous => keepPresence(previous, []));

    const presencePath = `presence/${projectId}`;
    const myPath = `${presencePath}/${user.uid}`;
    const presenceListRef = ref(db, presencePath);
    const myRef = ref(db, myPath);
    myPresenceRef.current = myRef;

    // Firebase RTDB rejects undefined values — omit photoURL if absent
    const presenceData: Record<string, any> = {
      displayName: user.displayName || user.email || 'Anonymous',
      color: collaboratorColor(user.uid),
      lastSeen: Date.now(),
      isActive: true,
    };
    if (user.photoURL) {
      presenceData.photoURL = user.photoURL;
    }

    // Set my presence
    set(myRef, { userId: user.uid, ...presenceData }).catch(() => {});

    // Remove presence on disconnect
    onDisconnect(myRef).remove().catch(() => {});

    // Heartbeat
    heartbeatRef.current = setInterval(() => {
      set(myRef, {
        userId: user.uid,
        ...presenceData,
        lastSeen: Date.now(),
      }).catch(() => {});
    }, HEARTBEAT_INTERVAL);

    // Listen to all presence for this project
    const stopListening = onValue(presenceListRef, (snapshot) => {
      const others = presentUsers(snapshot.val(), Date.now()).filter(present => present.userId !== user.uid);
      setOtherUsers(previous => keepPresence(previous, others));
    });

    return () => {
      // (off() with what onValue returns removes nothing: the listener of every workspace left stayed on)
      stopListening();
      cleanup();
    };
  }, [projectId, enabled, cleanup]);

  return { otherUsers };
}
